import { Box } from "@mui/material";
import { useCreateLicenseMutation } from "Api/Mutations/Licenses/useCreateLicenseMutation";
import { useLicenseQuery } from "Api/Queries/Licenses/useLicenseQuery";
import { usePartyLicensesQuery } from "Api/Queries/Licenses/usePartyLicensesQuery";
import { AffiliateCodeField } from "Components/Affiliate/AffiliateCodeField/AffiliateCodeField";
import { useHunterAffiliateCode } from "Components/Affiliate/AffiliateCodeField/useHunterAffiliateCode";
import { isCouponError } from "Components/Licenses/CouponInput/couponErrors";
import { LicenseOrderForm } from "Components/Licenses/LicenseOrderForm/LicenseOrderForm";
import { LicenseOrderFormSkeleton } from "Components/Licenses/LicenseOrderForm/LicenseOrderFormSkeleton";
import type { LicenseSelectionModel } from "Components/Licenses/LicenseOrderForm/schema";
import {
  getLicenseResumeState,
  getNextAvailableLicense,
  isSameAsOrderedLicense,
  transformLicenseToDefaultValues,
} from "Components/Licenses/licenseUtils";
import { OwnedLicenseContent } from "Components/Onboarding/BusinessEntity/Steps/LicenseSelectionStep/OwnedLicenseContent";
import { useEnsureUserRegion } from "Hooks/useEnsureUserRegion";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { OwnershipType } from "Infrastructure/Api/Api";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";
import { ErrorText } from "Components/Shared/Errors/ErrorText";

const LIFETIME_PERIODICITY = null;

const COMPONENT_RESOURCES =
  Resources.Onboarding.BusinessOnboarding.Steps.LicenseSelection;

type Props = {
  onNextStepRequested: () => void;
  // The Onboarding Hunter role of the add-role wizard. Set, the step offers the affiliate code
  // field (ZS-1024): a code attached here switches the offer to the affiliate price and the
  // backend re-prices an unpaid order priced without it.
  affiliateIdentityUserPublicID?: string;
};

export const LicenseSelectionStep = ({
  onNextStepRequested,
  affiliateIdentityUserPublicID,
}: Props) => {
  const user = useLoggedInUser();
  const { t } = useResource();
  const affiliateCode = useHunterAffiliateCode(affiliateIdentityUserPublicID);
  const {
    data: existingLicenses,
    isLoading,
    isError,
  } = usePartyLicensesQuery(user?.partyPublicID);

  const { mutate: createLicense, isPending: isCreateLicensePending } =
    useCreateLicenseMutation({
      onSuccess: onNextStepRequested,
      onError: (error) => {
        const couponError = isCouponError(error);
        if (couponError) {
          toast.error(
            t(Resources.Components.LicenseOrder.Coupon.Error[couponError]),
          );
        } else {
          toast.error(t(Resources.Errors.RequestError));
        }
      },
    });

  // The user's chosen market drives both the offered licenses and the preselected region —
  // never a CZ constant; a region-less user picks in the form and the offer loads on selection.
  const userRegion = user?.region ?? undefined;
  const { ensureUserRegion, isSettingRegion } = useEnsureUserRegion();
  const {
    data: { items: licenses } = {},
    isLoading: isLicensesLoading,
    isError: isLicensesError,
  } = useLicenseQuery(userRegion);

  if (isLoading || isLicensesLoading) {
    return <LicenseOrderFormSkeleton />;
  }

  if (isError || isLicensesError || (userRegion && !licenses)) {
    return <ErrorText>{t(Resources.Errors.RequestError)}</ErrorText>;
  }

  const resumeState = getLicenseResumeState(existingLicenses?.items ?? []);

  if (resumeState.kind === "paid") {
    return (
      <OwnedLicenseContent
        license={resumeState.license}
        onContinue={onNextStepRequested}
      />
    );
  }

  // created license, that haven't been paid yet
  const existingLicense =
    resumeState.kind === "unpaid" ? resumeState.unpaidLicenses[0] : undefined;
  const defaultLicense = licenses
    ? getNextAvailableLicense(licenses)
    : undefined;
  const defaultValues: Partial<LicenseSelectionModel> = existingLicense
    ? transformLicenseToDefaultValues(existingLicense)
    : {
        code: defaultLicense?.code,
        region: userRegion,
      };

  const onSubmit = (model: LicenseSelectionModel) => {
    // Nothing changed since the order was placed — just show its payment details again
    // instead of cancelling and re-creating the same order.
    if (existingLicense && isSameAsOrderedLicense(model, existingLicense)) {
      onNextStepRequested();
      return;
    }

    const {
      code,
      region,
      walletAddress = "",
      isUsingPrivateWallet,
      couponCode,
    } = model;
    const handleCreateLicense = () =>
      createLicense({
        code,
        walletAddress,
        region,
        ownershipType: isUsingPrivateWallet
          ? OwnershipType.NonCustodial
          : OwnershipType.Custodial,
        subscriptionPeriodicity: LIFETIME_PERIODICITY,
        shouldReplaceUnpaidLicenses: true,
        couponCode: couponCode || null,
      });
    const handleRegionError = () =>
      toast.error(t(Resources.Components.RegionRequiredModal.Error));

    ensureUserRegion(region, handleCreateLicense, handleRegionError);
  };

  return (
    <Box display="flex" flexDirection="column" height="100%" gap={6}>
      {t(COMPONENT_RESOURCES.Description)}
      <LicenseOrderForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        isSubmitting={isCreateLicensePending || isSettingRegion}
        isRegionSelectionDisabled={!!existingLicense}
        orderedLicense={existingLicense}
        hasCarouselEdgeBleed
        pricingKey={affiliateCode.pricingKey}
        affiliateCodeField={
          affiliateCode.isEnabled && (
            <AffiliateCodeField {...affiliateCode.fieldProps} />
          )
        }
      />
    </Box>
  );
};
